import { brand, legacy } from './ui/theme/semanticTokens';
import { alphaOf } from './utils/ui/colorAlpha';
import {
  badge as controlBadge,
  buttonDanger,
  buttonPrimary,
  buttonSecondary,
  buttonSuccess,
  field,
} from './ui/controls';
import {
  dataPanel as dataSurfacePanel,
  sectionCard as dataSectionCard,
  mobileSectionCard as dataMobileSectionCard,
  emptyState as dataEmptyState,
} from './ui/dataSurfaceStyles';

export const ui = {
  colors: {
    // Modern navy-neutral SaaS palette
    bg: legacy.bg,
    surface: legacy.surface,
    surfaceAlt: legacy.surfaceAlt,
    surfaceMuted: legacy.surfaceMuted,
    primary: legacy.primary,
    primaryDark: legacy.primaryDark,
    primaryLight: legacy.primaryLight,
    primarySoft: legacy.primarySoft,
    secondary: legacy.secondary,
    secondaryLight: legacy.secondaryLight,
    secondarySoft: legacy.secondarySoft,
    accent: legacy.accent,
    accentSoft: legacy.accentSoft,
    border: legacy.border,
    borderLight: legacy.borderLight,
    text: legacy.text,
    textMuted: legacy.textMuted,
    textLight: legacy.textLight,
    success: legacy.success,
    successLight: legacy.successLight,
    successSoft: legacy.successSoft,
    warning: legacy.warning,
    warningText: legacy.warningText,
    warningLight: legacy.warningLight,
    warningSoft: legacy.warningSoft,
    danger: legacy.danger,
    dangerLight: legacy.dangerLight,
    dangerSoft: legacy.dangerSoft,
    purple: legacy.purple,
    purpleSoft: legacy.purpleSoft,
    gradient: `linear-gradient(135deg, ${legacy.primaryDark} 0%, ${legacy.primary} 100%)`,
    gradientPrimary: `linear-gradient(135deg, ${legacy.primaryDark} 0%, ${legacy.primary} 100%)`,
  },
  radius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    pill: '999px',
  },
  shadow: {
    soft: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    medium: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
    strong: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
    focus: `0 0 0 3px ${alphaOf(brand.primary, 0.14)}`,
    glow: `0 0 20px ${alphaOf(brand.primary, 0.16)}`,
  },
  transition: {
    fast: 'all 0.15s cubic-bezier(0.16, 1, 0.3, 1)',
    normal: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
    slow: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  page: {
    // Data-page cap (dashboards, tables, route board). Raised 1400→1760 so a
    // collapsed nav rail actually widens content instead of just adding side
    // whitespace; ultra-wide/4K screens still get readable side margins.
    // Focused single forms (e.g. log-job) override this to a tighter width.
    maxWidth: '1760px',
    contentMaxWidth: '900px',
    // Canonical DESKTOP content-frame padding (top · right · bottom · left).
    // Sides are 28px + safe-area-aware so every role's dashboard sits at the
    // SAME margin. admin/platform were 28px; billing/worker/supervisor had
    // drifted to 24px (and billing also capped width at 1400 not 1760) — this
    // is the single source so it can't drift again. (16/20 == spacePx 4/5.)
    // Mobile/tablet padding stays role-local — the shells genuinely differ.
    desktopContentPadding:
      '16px max(28px, env(safe-area-inset-right, 0px)) 20px max(28px, env(safe-area-inset-left, 0px))',
  },
  typography: {
    fontFamily: "'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
  },
};

/** Token-backed surfaces — see `src/ui/dataSurfaceStyles.js` */
export const card = {
  ...dataSurfacePanel,
  transition: ui.transition.normal,
};

export const sectionCard = {
  ...dataSectionCard,
};

export const mobileSectionCard = {
  ...dataMobileSectionCard,
};

/** Centered empty / placeholder block */
export const emptyState = {
  ...dataEmptyState,
};

/** @see src/ui/controls.js — token-aligned with premium light shell */
export const primaryButton = { ...buttonPrimary };

export const secondaryButton = { ...buttonSecondary };

export const dangerButton = { ...buttonDanger };

export const successButton = { ...buttonSuccess };

export const inputBase = { ...field };

const BRANDING_DESCRIPTION_POOL =
  'Professional field service management platform designed for pool maintenance companies. Track jobs, manage teams, generate invoices, and grow your business with powerful analytics.';

const BRANDING_DESCRIPTION_NEUTRAL =
  'Professional field service management platform for your operations. Track jobs, manage teams, generate invoices, and grow your business with powerful analytics.';

/** Use with tenant `industryType` from AuthContext; null treated as pool for safe defaults. */
export const getBrandingDescriptionForIndustry = (industryType) =>
  industryType == null || industryType === 'pool' ? BRANDING_DESCRIPTION_POOL : BRANDING_DESCRIPTION_NEUTRAL;

export const branding = {
  appName: 'CrewVex',
  companyName: 'CrewVex',
  companyLegalName: 'CrewVex',
  tagline: 'The service business operating system',
  description: BRANDING_DESCRIPTION_POOL,
  supportEmail: 'support@crewvex.com',
  supportPhone: '(888) 555-0000',
  phone: '(631) 828-9748',
  email: 'info@crewvex.com',
  address: 'P.O. BOX 1327, East Hampton NY 11937',
  // Empty on purpose: a tenant's documents print only the company's own footer, never CrewVex's.
  legalFooter: '',
  companyLogoUrl: null,
  logoUrl: null,
  taxState: '',
  taxEnabled: false,
  taxRate: null,
  invoiceTheme: 'clean',
  invoiceAccentColor: null,
  defaultLocale: 'en',
  aiPostJobBehavior: 'ask',
  // null = admin never chose a retention period = keep every photo (2026-10-08).
  photoRetentionDays: null,
};

// Modern animation utilities
export const animations = {
  fadeIn: {
    animation: 'fadeIn 0.3s ease-in-out',
  },
  slideIn: {
    animation: 'slideIn 0.2s ease-out',
  },
  bounceIn: {
    animation: 'bounceIn 0.4s ease-out',
  },
};

// Modern loading spinner
export const loadingSpinner = {
  display: 'inline-block',
  width: '20px',
  height: '20px',
  border: `2px solid ${ui.colors.border}`,
  borderRadius: '50%',
  borderTopColor: ui.colors.primary,
  animation: 'spin 1s ease-in-out infinite',
};

/** @see src/ui/controls.js */
export const badge = { ...controlBadge };

// Status indicators
export const statusIndicator = {
  online: {
    ...badge,
    backgroundColor: ui.colors.successSoft,
    color: ui.colors.success,
  },
  offline: {
    ...badge,
    backgroundColor: ui.colors.textLight,
    color: ui.colors.surface,
  },
  pending: {
    ...badge,
    backgroundColor: ui.colors.warningSoft,
    color: ui.colors.warningText,
  },
};